import { motion } from 'framer-motion';
import { Globe, Smartphone, Search, MapPin, Palette, Wrench } from 'lucide-react';

const services = [
  {
    icon: Globe,
    title: 'Custom Websites',
    description: 'Built from scratch for your business. Not a template.',
    tag: 'CORE',
    delay: 0,
  },
  {
    icon: Smartphone,
    title: 'Mobile-First',
    description: 'Looks perfect on phones. Because that\'s where your customers are.',
    tag: 'ESSENTIAL',
    delay: 0.08,
  },
  {
    icon: Search,
    title: 'SEO Built In',
    description: 'Show up when people search. We handle the technical stuff.',
    tag: 'GROWTH',
    delay: 0.15,
  },
  {
    icon: MapPin,
    title: 'Google Business',
    description: 'Get found locally. Maps, reviews, the whole thing.',
    tag: 'LOCAL',
    delay: 0.1,
  },
  {
    icon: Palette,
    title: 'Brand Design',
    description: 'Colors, fonts, logo placement. We make it look right.',
    tag: 'DESIGN',
    delay: 0.18,
  },
  {
    icon: Wrench,
    title: 'We Maintain It',
    description: 'Updates, fixes, changes. You never have to touch it.',
    tag: 'SUPPORT',
    delay: 0.12,
  },
];

export const ServicesSection = () => {
  return (
    <section id="services" className="py-28 md:py-36 bg-[#0a0a0a] relative overflow-hidden" data-testid="services-section">
      {/* Background */}
      <div className="absolute inset-0 cyber-grid opacity-[0.03]" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header - left aligned */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-16 max-w-xl"
        >
          <p className="text-[#e62020] text-xs tracking-[0.2em] uppercase mb-5">
            What we do
          </p>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] text-white"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            data-testid="services-headline"
          >
            We build it.
            <br />
            <span className="text-[#888888]">You run your business.</span>
          </h2>
        </motion.div>

        {/* Services Grid - asymmetric feel */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ 
                duration: 0.45 + (index * 0.03), 
                delay: service.delay,
                ease: 'easeOut'
              }}
              whileHover={{ y: -4, transition: { duration: 0.25 } }}
              className={`group relative bg-[#111111] border border-[#1a1a1a] p-7 hover:border-[#e62020]/40 transition-colors ${
                index === 2 ? 'md:translate-y-4' : ''
              } ${index === 4 ? 'md:-translate-y-2' : ''}`}
              data-testid={`service-card-${index}`}
            >
              {/* Tag - positioned differently */}
              <span className="absolute top-5 right-5 text-[9px] tracking-wider text-[#888888] group-hover:text-[#e62020] transition-colors">
                {service.tag}
              </span>
              
              {/* Icon */}
              <div className="mb-5">
                <div className="w-10 h-10 flex items-center justify-center border border-[#262626] group-hover:border-[#e62020]/50 transition-colors">
                  <service.icon size={18} className="text-[#e62020]" />
                </div>
              </div>

              {/* Content */}
              <h3
                className="text-xl font-bold uppercase tracking-tight text-white mb-2"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                {service.title}
              </h3>
              <p className="text-[#888888] text-sm leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Bottom note - casual */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-[#888888] text-sm mt-12 ml-1"
        >
          Need something specific? <a href="#contact" className="text-white hover:text-[#e62020] transition-colors">Just ask.</a>
        </motion.p>
      </div>
    </section>
  );
};
